import { PureComponent } from "react";
import { withGenericConfig } from "/contexts/generic-config-context";
import { trackEvent } from "/services/bigbrain-service";
import {
  SEGMENT_LAYOUT_NOT_FOUND,
  SEGMENT_NOT_VISIBLE_IN_VIEWPORT,
  SEGMENT_VISIBLE_IN_VIEWPORT
} from "/constants/bigbrain-event-types";
import { withPreviewBaseSegment } from "contexts/preview-base-segment-context";
import { getTheme, themes } from "segments/segments-service";
import Segments from "segments/desktop";
import classnames from "classnames";
import colors from "styles/colors";
import { SPACE_BLUE_COLOR_NAME } from "styles/color-consts";
import VisibilitySensor from "react-visibility-sensor";
import { hexToRgba, isServerSide } from "utils/general";
import { StyledBaseLayoutSegmentComponent, triangleHeightFactor } from "./base-layout-segment-component.scss";

const PERCENTAGE_REGEX = /^\d+(\.\d+)?%$/;
const getGradientThemeStyles = (gradientColors = []) => ({
  background: `linear-gradient(180deg, ${gradientColors
    .map(({ color }) => {
      const backgroundColor = getTheme(color)?.background;
      return backgroundColor ? `${hexToRgba(backgroundColor, 1)}` : "";
    })
    .join(", ")})`,
  color: getTheme(gradientColors[0]?.color)?.color
});

class BaseLayoutSegmentComponent extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isInView: false
    };
  }

  getSegmentComponent() {
    const { segment } = this.props;
    const segmentComponent = Segments[segment.type]?.component;

    if (!segmentComponent) {
      trackEvent(SEGMENT_LAYOUT_NOT_FOUND, { kind: segment.type });
      return false;
    }

    return segmentComponent;
  }

  getHtmlComponentElementFromDom() {
    const baseLayoutComponentClassName = this.getBaseLayoutComponentClassName();
    const ssrComponent = document.querySelector(`.${baseLayoutComponentClassName}`);
    return ssrComponent ? ssrComponent.innerHTML : null;
  }

  getSegmentStyle(segmentProps) {
    let { paddingTop, paddingBottom, segmentMinHeight } = segmentProps;
    const { theme, gradientTheme, mobilePaddingTop, mobilePaddingBottom, baseLayoutStyleProps } = segmentProps;
    const { isMobileOrTabletBrowser } = this.props;
    const themeStyles = gradientTheme?.length ? getGradientThemeStyles(gradientTheme) : themes[theme];

    if (isMobileOrTabletBrowser) {
      paddingTop = mobilePaddingTop || paddingTop;
      paddingBottom = mobilePaddingBottom || paddingBottom;
    }

    return {
      ...themeStyles,
      ...baseLayoutStyleProps,
      paddingTop,
      paddingBottom,
      minHeight: segmentMinHeight
    };
  }

  getBaseLayoutComponentClassName() {
    const { index } = this.props;
    return `base-layout-segment-component-${index}`;
  }

  getTriangleBorderStyle(triangle) {
    const color = colors[triangle?.color] || colors[SPACE_BLUE_COLOR_NAME];
    return `${100 * triangleHeightFactor}vw solid ${color}`;
  }

  onVisibilityChange = (isVisible) => {
    const { index, segment } = this.props;
    const visibilityEvent = isVisible ? SEGMENT_VISIBLE_IN_VIEWPORT : SEGMENT_NOT_VISIBLE_IN_VIEWPORT;
    const { visibilityOffset, useInfinitely } = segment.props.visibilitySensorProps || {};

    !useInfinitely && isVisible && this.setState({ isInView: true });
    useInfinitely && this.setState({ isInView: isVisible });

    trackEvent(visibilityEvent, {
      kind: segment.type,
      info1: visibilityOffset,
      info2: window.scrollY,
      placement: index
    });
  };

  getOffset = (offset) => {
    const isOffsetInPercentages = PERCENTAGE_REGEX.test(offset);

    if (!isServerSide() && isOffsetInPercentages) {
      return (parseInt(offset) / 100) * window.innerHeight;
    }

    return parseInt(offset);
  };

  render() {
    const { segment, isUserLoggedIn, extraProps, isMobileBrowser, dynamicProps } = this.props;
    const { isInView } = this.state;
    if (!segment) return null;

    const { segmentId, hideOnMobile, topTriangle, bottomTriangle, visibilitySensorProps, ...restSegmentProps } =
      segment.props;

    const { useVisibilitySensor, visibilityOffset, easeInEffect } = visibilitySensorProps || {};

    if (hideOnMobile && isMobileBrowser) return null; // this segment needs to be hidden in mobile views

    const SegmentComponent = this.getSegmentComponent();
    if (!SegmentComponent) return null;

    const SegmentComponentWithGenericConfig = withGenericConfig(SegmentComponent);
    const segmentStyle = this.getSegmentStyle(segment.props);

    const topTriangleBorderBottom = this.getTriangleBorderStyle(topTriangle);
    const bottomTriangleBorderTop = this.getTriangleBorderStyle(bottomTriangle);

    const parsedVisibilityOffset = this.getOffset(visibilityOffset);

    return (
      <VisibilitySensor
        offset={{ bottom: parsedVisibilityOffset || 0, top: parsedVisibilityOffset || 0 }}
        partialVisibility={true}
        onChange={this.onVisibilityChange}
        active={!!useVisibilitySensor}
        scrollCheck={!!useVisibilitySensor}
      >
        <StyledBaseLayoutSegmentComponent
          id={segmentId}
          className={classnames(
            "base-layout-segment-component",
            { "ease-in": easeInEffect },
            { "ease-in-animation": isInView && easeInEffect },
            this.getBaseLayoutComponentClassName()
          )}
          style={segmentStyle}
        >
          {topTriangle?.isVisible && (
            <div
              className={classnames("triangle", "top", "bottom-left-corner")}
              style={{ borderBottom: topTriangleBorderBottom }}
            />
          )}
          <div className="segment-wrapper">
            <SegmentComponentWithGenericConfig
              {...extraProps}
              {...restSegmentProps}
              dynamicProps={dynamicProps}
              isUserLoggedIn={isUserLoggedIn}
              segmentType={segment.type}
              isInView={isInView}
            />
          </div>
          {bottomTriangle?.isVisible && (
            <div
              className={classnames("triangle", "bottom", "top-right-corner")}
              style={{ borderTop: bottomTriangleBorderTop }}
            />
          )}
        </StyledBaseLayoutSegmentComponent>
      </VisibilitySensor>
    );
  }
}

BaseLayoutSegmentComponent.defaultProps = {
  extraProps: {}
};

const BaseLayoutSegmentComponentContainer = (props) => {
  const Component = props.isPreview
    ? withPreviewBaseSegment(BaseLayoutSegmentComponent)
    : withGenericConfig(BaseLayoutSegmentComponent);

  return <Component {...props} />;
};

export default BaseLayoutSegmentComponentContainer;
